import * as _ from 'lodash';
import * as moment from 'moment';
import { Component } from 'react';
import { Dispatch, connect } from 'react-redux';
import { IApplicationState, RecipientActionCreators, AppActionCreators, IEmpletMMDataResponse } from '@nkr/store/index';
import { IEmpletAllele, IEpletMismatch } from '@nkr/models/index';
import { downloadFile } from '@nkr/services/download-file';
import RecipientNav from '../../recipients/recipient-nav';
import AllelesSearched from './alleles-searched';
import MolecularMismatchRisk from './molecular-mismatch-risk';
import CumulativeEpletMismatchLevel from './cumulative-eplet-mismatch-level';
import MismatchResults from './mismatch-results';
import Sources from './sources';

interface IProps {
    recipientId: number,
    donorId: number,
    centerId: number
}

interface IStateProps {
    groupIsSuper: boolean
}

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getEpletMmData: (recipientId: number, donorId: number) => Promise<IEmpletMMDataResponse>,
    downloadFile: (recipientId: number, donorId: number) => Promise<any>
}

type TProps = IStateProps & IDispatchProps & IProps;

interface IState {
    donorAlias: string,
    recipientAlias: string,
    recipientAlleles: IEmpletAllele[],
    donorAlleles: IEmpletAllele[],
    epletMismatches: IEpletMismatch[],
    errors: string[],
    singleEpletMmDr: number,
    singleEpletMmDq: number,
    singleEpletMmDqLow: number,
    totalEpletMmDr: number,
    totalEpletMmDrVerifiedAb: number,
    totalEpletMmDq: number,
    totalEpletMmDqVerifiedAb: number
}

class RecipientEmpletMM extends Component<TProps, IState> {
    readonly state: IState = {
        donorAlias: '',
        recipientAlias: '',
        recipientAlleles: [],
        donorAlleles: [],
        epletMismatches: [],
        errors: [],
        singleEpletMmDr: -1,
        singleEpletMmDq: -1,
        singleEpletMmDqLow: -1,
        totalEpletMmDr: -1,
        totalEpletMmDrVerifiedAb: -1,
        totalEpletMmDq: -1,
        totalEpletMmDqVerifiedAb: -1
    }

    componentDidMount = () => {
        this.getData();
    }

    downloadFile = (recipientId: number, donorId: number) => {
        this.props.downloadFile(recipientId, donorId)
            .then((response: any) => {
                let fileName = "mismatchResults" + "_" + moment().format("YYYY_MM_DD");

                downloadFile(response.data, fileName, response.data.type);
            });
    }

    getData = () => {
        this.props.showLoading('Getting eplet mm data, one moment please...').then(() => {
            this.props.getEpletMmData(this.props.recipientId, this.props.donorId).then(response => {
                if (response.data.success) {
                    let {
                        recipientAlleles,
                        donorAlleles,
                        epletMismatches,
                        errors,
                        singleEpletMmDr,
                        singleEpletMmDq,
                        singleEpletMmDqLow,
                        totalEpletMmDr,
                        totalEpletMmDrVerifiedAb,
                        totalEpletMmDq,
                        totalEpletMmDqVerifiedAb } = response.data.results.result;

                    let { donorAlias,
                        recipientAlias,
                    } = response.data.results;


                    this.setState({
                        donorAlias,
                        recipientAlias,
                        recipientAlleles,
                        donorAlleles,
                        epletMismatches,
                        errors,
                        singleEpletMmDr,
                        singleEpletMmDq,
                        singleEpletMmDqLow,
                        totalEpletMmDr,
                        totalEpletMmDrVerifiedAb,
                        totalEpletMmDq,
                        totalEpletMmDqVerifiedAb
                    });
                }

                this.props.hideLoading();
            });
        });
    };

    render = () => {
        const recipientAlias = this.state.recipientAlias != null ? this.state.recipientAlias : '';

        return (
            <>
                <div style={{ padding: 10 }}>
                    <RecipientNav
                        activeKey={'EPLET-MM'}
                        isAdmin={false}
                        isNkrLogin={this.props.groupIsSuper}
                        centerId={this.props.centerId}
                        recipientId={this.props.recipientId}
                        donorId={this.props.donorId}
                        recipientAlias={recipientAlias}
                    />

                    <div style={{ borderWidth: '0px 1px 1px 1px', borderStyle: 'solid', borderColor: '#ccc', borderBottomLeftRadius: 4, borderBottomRightRadius: 4 }}>
                        {recipientAlias != "" && (
                            <>
                                <div className='container-fluid' style={{ paddingTop: 20 }}>
                                    <AllelesSearched
                                        recipientId={this.props.recipientId}
                                        donorId={this.props.donorId}
                                        recipientAlias={this.state.recipientAlias}
                                        donorAlias={this.state.donorAlias}
                                        recipientAlleles={this.state.recipientAlleles}
                                        donorAlleles={this.state.donorAlleles}
                                        downloadFile={this.downloadFile}
                                    />
                                </div>

                                <div className='container-fluid' style={{ paddingTop: 10 }}>
                                    <MolecularMismatchRisk
                                        recipientId={this.props.recipientId}
                                        donorId={this.props.donorId}
                                        recipientAlias={this.state.recipientAlias}
                                        donorAlias={this.state.donorAlias}
                                        recipientAlleles={this.state.recipientAlleles}
                                        donorAlleles={this.state.donorAlleles}
                                    />
                                </div>

                                <div className='container-fluid' style={{ paddingTop: 10 }}>
                                    <CumulativeEpletMismatchLevel
                                        recipientId={this.props.recipientId}
                                        donorId={this.props.donorId}
                                        recipientAlias={this.state.recipientAlias}
                                        donorAlias={this.state.donorAlias}
                                        totalEpletMmDr={this.state.totalEpletMmDr}
                                        totalEpletMmDq={this.state.totalEpletMmDq}
                                    />
                                </div>

                                <div className='container-fluid' style={{ paddingTop: 10 }}>
                                    <MismatchResults
                                        recipientId={this.props.recipientId}
                                        donorId={this.props.donorId}
                                        recipientAlias={this.state.recipientAlias}
                                        donorAlias={this.state.donorAlias}
                                        epletMismatches={this.state.epletMismatches}
                                    />
                                </div>

                                <div className='container-fluid' style={{ paddingTop: 10, paddingBottom: 10 }}>
                                    <Sources />
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </>
        );
    }
}

const mapStateToProps = (appState: IApplicationState): IStateProps => {
    return {
        groupIsSuper: appState.app.user.group.isSuper
    }
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        getEpletMmData: (recipientId, donorId) => {
            return dispatch(RecipientActionCreators.getEpletMmData(recipientId, donorId));
        },
        downloadFile: (recipientId, donorId) => {
            return dispatch(RecipientActionCreators.downloadFile(recipientId, donorId));
        },
    };
}

export default connect<IStateProps, IDispatchProps, IProps>(
    mapStateToProps, mapDispatchToProps)(RecipientEmpletMM)